import { useQueryClient } from '@tanstack/react-query'
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { useNotificationsQuery } from '@unsw/api/queries/notifications'
import { getAdminNavigationTabs } from '@unsw/features/admin-dashboard'
import { getTeacherNavigationTabs } from '@unsw/features/teacher-dashboard'
import { SidebarProvider } from '@unsw/hooks'
import { useUser } from '@unsw/hooks'
import { useAuthStore } from '@unsw/store/auth'
import { useNotificationStore, useNotificationWebSocket } from '@unsw/store/notifications'
import { AnimatedTabs } from '@unsw/ui/components/composed/animated-tabs'
import { Footer } from '@unsw/ui/components/composed/footer'
import { Header, HeaderUserMenu } from '@unsw/ui/components/composed/header'
import { HeaderNotifications } from '@unsw/ui/components/composed/header-notifications'
import { Button } from '@unsw/ui/components/ui/button'
import { Toaster } from '@unsw/ui/components/ui/sonner'
import { useEffect, useMemo } from 'react'
import type * as React from 'react'
import logo from '../assets/images/mentorai_yellow.png'

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? 's' : ''} ago`
  const diffDays = Math.floor(diffHr / 24)
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
}

export const Route = createFileRoute('/dashboard')({
  // Auth guard - redirect to login if not authenticated
  beforeLoad: ({ location }) => {
    const tokens = useAuthStore.getState().tokens
    if (!tokens?.access_token) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href, // Remember where user wanted to go
        },
      })
    }
  },
  component: DashboardLayout,
})

// Helper to build student navigation tabs with optional recordingId
function buildStudentNavigationTabs(recordingId?: number) {
  const summaryHref = recordingId
    ? `/dashboard/summary?recordingId=${recordingId}`
    : '/dashboard/summary'
  const feedbackHref = recordingId
    ? `/dashboard/feedback?recordingId=${recordingId}`
    : '/dashboard/feedback'

  return [
    { label: 'Progress', value: 'progress', href: '/dashboard' },
    { label: 'Recordings', value: 'recordings', href: '/dashboard/recordings' },
    { label: 'Summary', value: 'summary', href: summaryHref },
    { label: 'Feedback', value: 'feedback', href: feedbackHref },
    { label: 'Resources', value: 'resources', href: '/dashboard/resources' },
  ]
}

// Custom link component for AnimatedTabs that works with TanStack Router
function TabLink({
  to,
  children,
  className,
  onPointerEnter,
  onFocus,
  onClick,
}: {
  to: string
  children: React.ReactNode
  className?: string
  onPointerEnter?: () => void
  onFocus?: () => void
  onClick?: () => void
}) {
  return (
    <Link
      to={to}
      className={className}
      onPointerEnter={onPointerEnter}
      onFocus={onFocus}
      onClick={onClick}
    >
      {children}
    </Link>
  )
}

function DashboardLayout() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, logout } = useAuthStore()
  const { isAdmin, isTeacher } = useUser()
  const routerState = useRouterState()

  // Determine active tab based on current route and user role
  const currentPath = routerState.location.pathname
  const getActiveTab = () => {
    // Admin routes
    if (currentPath.startsWith('/dashboard/usage')) return 'usage'
    if (currentPath.startsWith('/dashboard/performance')) return 'performance'
    if (currentPath.startsWith('/dashboard/ai-feedback')) return 'ai-feedback'
    // Teacher routes
    if (currentPath.startsWith('/dashboard/teacher-feedback')) return 'teacher-feedback'
    if (currentPath.startsWith('/dashboard/teacher-test-result')) return 'teacher-test-result'
    if (currentPath.startsWith('/dashboard/teacher-survey')) return 'teacher-survey'
    if (currentPath.startsWith('/dashboard/teacher-quizzes')) return 'teacher-quizzes'
    // Student routes
    if (currentPath === '/dashboard' || currentPath === '/dashboard/') {
      // Default route for different roles
      if (isAdmin) return 'usage'
      if (isTeacher) return 'teacher-feedback'
      return 'progress'
    }
    if (currentPath.startsWith('/dashboard/recordings')) return 'recordings'
    if (currentPath.startsWith('/dashboard/summary')) return 'summary'
    if (currentPath.startsWith('/dashboard/feedback')) return 'feedback'
    if (currentPath.startsWith('/dashboard/resources')) return 'resources'
    return 'progress'
  }
  const activeTab = getActiveTab()

  // Get search params for tab navigation
  const searchParams = routerState.location.search as {
    recordingId?: string
    requestId?: string
  }
  const recordingId = searchParams.recordingId ? Number(searchParams.recordingId) : undefined
  const requestId = searchParams.requestId ? Number(searchParams.requestId) : undefined

  // Build navigation tabs based on user role
  const navigationTabs = useMemo(() => {
    if (isAdmin) {
      return getAdminNavigationTabs()
    }
    if (isTeacher) {
      return getTeacherNavigationTabs(requestId)
    }
    return buildStudentNavigationTabs(recordingId)
  }, [isAdmin, isTeacher, recordingId, requestId])

  const handleLogout = () => {
    logout()
    navigate({ to: '/login' })
    queryClient.clear()
  }

  // Notification system
  useNotificationWebSocket()
  const { notifications, setNotifications, markAsRead, markAllAsRead } = useNotificationStore()
  const tokens = useAuthStore((s) => s.tokens)
  const { data: notificationsData } = useNotificationsQuery({ enabled: !!tokens?.access_token })

  // Sync API notifications into store on initial load
  useEffect(() => {
    if (notificationsData) {
      setNotifications(
        notificationsData.map((n) => ({
          id: String(n.id),
          title: n.message_title,
          message: n.message_body_text,
          link: n.message_body_link,
          time: formatTimeAgo(n.created_at),
          read: n.is_read_at !== null,
        })),
      )
    }
  }, [notificationsData, setNotifications])

  return (
    <div className="relative min-h-screen flex flex-col bg-white overflow-hidden">
      {/* Background Shape */}
      <svg
        className="absolute pointer-events-none fill-unsw-yellow"
        viewBox="0 0 60 60"
        aria-hidden="true"
        style={{ top: 0, left: '-16%', height: '55vw' }}
      >
        <polygon points="0 0 0 48.533488 39.4213208 60 57.4153242 35.7397558 32.0559823 0" />
      </svg>

      {/* Top Header */}
      <Header
        className="relative z-10 bg-white"
        sticky
        logo={
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={logo} alt="Mentor AI" className="h-6 md:h-8" />
          </Link>
        }
        actions={
          <>
            <HeaderNotifications
              notifications={notifications}
              onNotificationClick={(notification) => {
                markAsRead(notification.id)
                if (notification.link) {
                  const feedbackMatch = notification.link.match(/\/feedback\/(\d+)/)
                  if (feedbackMatch) {
                    navigate({
                      to: '/dashboard/summary',
                      search: { recordingId: Number(feedbackMatch[1]) },
                    })
                  } else {
                    navigate({ to: notification.link })
                  }
                }
              }}
              onMarkAllRead={() => {
                markAllAsRead()
              }}
            />
            <HeaderUserMenu
              name={user?.full_name ?? user?.username ?? undefined}
              manageAccountHref="/dashboard/profile"
            >
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </HeaderUserMenu>
          </>
        }
        mobileMenuContent={(closeMenu) => (
          <div className="flex flex-col gap-4">
            {/* Mobile Navigation Links */}
            <nav className="flex flex-col gap-2">
              {navigationTabs.map((tab) => (
                <Link
                  key={tab.value}
                  to={tab.href}
                  onClick={closeMenu}
                  className={`text-sm font-medium py-2 px-3 rounded-md transition-colors ${
                    activeTab === tab.value
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {tab.label}
                </Link>
              ))}
            </nav>
            {/* Mobile User Info & Logout */}
            <div className="border-t pt-4">
              {user && (
                <div className="mb-3 px-3">
                  <p className="font-medium text-sm">{user.full_name ?? user.username}</p>
                  <Link
                    to="/dashboard/profile"
                    onClick={closeMenu}
                    className="text-xs text-secondary hover:underline underline-offset-2 transition-colors"
                  >
                    Manage your account
                  </Link>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  closeMenu()
                  handleLogout()
                }}
                className="w-full"
              >
                Logout
              </Button>
            </div>
          </div>
        )}
      >
        {/* Animated Navigation Tabs */}
        <AnimatedTabs tabs={navigationTabs} initialTab={activeTab} LinkComponent={TabLink} />
      </Header>

      {/* Main content */}
      <main className="relative z-10 flex-1 p-0 md:p-6">
        <SidebarProvider>
          <Outlet />
        </SidebarProvider>
      </main>

      {/* Footer */}
      <Footer
        className="relative z-10"
        logo={<img src={logo} alt="Mentor AI" className="h-6 md:h-10 brightness-0 invert" />}
      />

      {/* Toast notifications */}
      <Toaster position="top-right" />
    </div>
  )
}
